/**
 * Organization API client
 * Requirements: 14.1-14.9 - Organization management
 */
import { createAuthenticatedClient } from '../../../shared/api/axiosClient';
import type {
  Company,
  Department,
  Employee,
  CreateCompanyDTO,
  UpdateCompanyDTO,
  CreateDepartmentDTO,
  UpdateDepartmentDTO,
  AssignEmployeeDTO,
} from '../types/organization.types';
import type { OrderItem } from '../../purchase/api/purchaseApi';

/** 社長向けアカウント情報（GET /company/account の応答）。 */
export interface AccountData {
  companyName: string;
  qrIssuedAt: string | null;
  order: { id: string; status: string; createdAt: string; items: OrderItem[] } | null;
}

/** 会社管理のCSV一括ダウンロード（GET /companies?detail=full）1行分。請求先・最新注文を含む。 */
export interface CompanyExportRow {
  id: string;
  name: string;
  hasJinzai: boolean;
  hasSoshiki: boolean;
  hasNavi: boolean;
  reportVisible: boolean;
  employeeReportButtonVisible: boolean;
  qrIssuedAt: string | null;
  createdAt: string;
  billing: {
    customerNumber: string | null;
    name: string;
    zip: string;
    address: string;
    tel: string;
    presidentName: string;
    email: string;
    settlementEmail: string | null;
    paymentMethodChoice: string;
    paymentMethodStatus: string;
    createdAt: string;
  } | null;
  latestOrder: {
    id: string;
    status: string;
    totalAmountIncluded: number;
    createdAt: string;
    items: {
      productCode: string;
      productName: string;
      userCount: number;
      paymentMethod: string;
      amountIncluded: number;
    }[];
  } | null;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

let apiClient: ReturnType<typeof createAuthenticatedClient> | null = null;

const getApiClient = () => {
  if (!apiClient) {
    apiClient = createAuthenticatedClient(API_BASE_URL, () => {
      window.location.href = '/login';
    });
  }
  return apiClient;
};

export const organizationApi = {
  // Company APIs
  async getCompanies(): Promise<Company[]> {
    const client = getApiClient();
    const response = await client.get<Company[]>('/companies');
    return response.data;
  },

  /** 全会社の明細（請求先・最新注文込み）を取得する（ADMIN・CSV一括ダウンロード用）。 */
  async getCompaniesExport(): Promise<CompanyExportRow[]> {
    const client = getApiClient();
    const response = await client.get<CompanyExportRow[]>('/companies', {
      params: { detail: 'full' },
    });
    return response.data;
  },

  async createCompany(data: CreateCompanyDTO): Promise<Company> {
    const client = getApiClient();
    const response = await client.post<Company>('/companies', data);
    return response.data;
  },

  async updateCompany(id: string, data: UpdateCompanyDTO): Promise<Company> {
    const client = getApiClient();
    const response = await client.put<Company>(`/companies/${id}`, data);
    return response.data;
  },

  async deleteCompany(id: string, confirmed: boolean = false): Promise<void> {
    const client = getApiClient();
    await client.delete(`/companies/${id}`, {
      params: { confirmed: confirmed.toString() },
    });
  },

  // 社長向けアカウント情報（自社の最新注文＋QR発行日）
  async getAccount(): Promise<AccountData> {
    const client = getApiClient();
    const response = await client.get<AccountData>('/company/account');
    return response.data;
  },

  // Department APIs
  async getDepartmentsByCompany(companyId: string): Promise<Department[]> {
    const client = getApiClient();
    const response = await client.get<Department[]>(`/companies/${companyId}/departments`);
    return response.data;
  },

  async createDepartment(
    companyId: string,
    data: Omit<CreateDepartmentDTO, 'companyId'>
  ): Promise<Department> {
    const client = getApiClient();
    const response = await client.post<Department>(`/companies/${companyId}/departments`, data);
    return response.data;
  },

  async updateDepartment(id: string, data: UpdateDepartmentDTO): Promise<Department> {
    const client = getApiClient();
    const response = await client.put<Department>(`/departments/${id}`, data);
    return response.data;
  },

  async deleteDepartment(id: string, confirmed: boolean = false): Promise<void> {
    const client = getApiClient();
    await client.delete(`/departments/${id}`, {
      params: { confirmed: confirmed.toString() },
    });
  },

  // Employee-Department APIs
  async getEmployeesByDepartment(departmentId: string): Promise<Employee[]> {
    const client = getApiClient();
    const response = await client.get<Employee[]>(`/departments/${departmentId}/employees`);
    return response.data;
  },

  async assignEmployeeToDepartment(
    departmentId: string,
    data: Omit<AssignEmployeeDTO, 'departmentId'>
  ): Promise<void> {
    const client = getApiClient();
    await client.post(`/departments/${departmentId}/employees`, data);
  },

  async removeEmployeeFromDepartment(departmentId: string, userId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`/departments/${departmentId}/employees/${userId}`);
  },
};
